import type { ComponentType } from 'react'
import { useEffect, useLayoutEffect, useState } from 'react'

import { createPortal } from 'react-dom'
import { Link, useLocation } from 'react-router-dom'

import {
  EventTabIcon,
  HomeTabIcon,
  MarketTabIcon,
  TeamTabIcon,
  WalletTabIcon,
} from '@/components/icons'
import { useI18n } from '@/hooks/useI18n'

type TabIcon = ComponentType<{ color?: string; className?: string }>

interface NavItem {
  href: string
  icon: TabIcon
  label: string
}

/** 需要展示底部胶囊导航的主 tab 路径白名单 */
const PRIMARY_PATHS: readonly string[] = ['/', '/activity', '/market', '/team', '/assets']

/**
 * 布局视口底部与可视视口底部的差值（移动浏览器工具栏/地址栏等）。
 * 写在 documentElement 的 `--vv-bottom-overlap`，与 `env(safe-area-inset-bottom)`、1.25rem 合成 `--bottom-nav-offset`。
 *
 * 注意：导航通过 Portal 挂到 `body`，`position: fixed` 相对真实视口，才能与 safe-area / visualViewport 一致；
 * 若留在带 `transform` 的 Shell 内，fixed 会相对列容器，safe-area 与 vv 补偿往往「看起来没效果」。
 */
function useVisualViewportBottomOverlap() {
  useLayoutEffect(() => {
    const root = document.documentElement
    const vv = window.visualViewport
    const sync = () => {
      if (!vv) {
        root.style.setProperty('--vv-bottom-overlap', '0px')
        return
      }
      const fromInner = window.innerHeight - vv.height - vv.offsetTop
      const fromClient = document.documentElement.clientHeight - vv.height - vv.offsetTop
      const overlap = Math.max(0, fromInner, fromClient)
      root.style.setProperty('--vv-bottom-overlap', `${overlap}px`)
    }

    sync()
    if (!vv) {
      return
    }

    const onRaf = () => requestAnimationFrame(sync)

    vv.addEventListener('resize', sync)
    vv.addEventListener('scroll', sync)
    vv.addEventListener('geometrychange', sync as EventListener)
    window.addEventListener('resize', onRaf)
    window.addEventListener('orientationchange', onRaf)

    return () => {
      vv.removeEventListener('resize', sync)
      vv.removeEventListener('scroll', sync)
      vv.removeEventListener('geometrychange', sync as EventListener)
      window.removeEventListener('resize', onRaf)
      window.removeEventListener('orientationchange', onRaf)
    }
  }, [])
}

/**
 * 由于 `MobileAppShell` 使用 `transform` 形成独立 stacking context，
 * 页面内的弹窗（`.fixed.inset-0.z-50`）只能在 Shell 内部浮起；
 * 而 BottomNav 通过 Portal 挂到 `body`，处于更高的根 stacking context。
 * 所以不能用 z-index 压下它 —— 观察 Shell 内部是否存在此类弹窗遮罩，
 * 有就把 BottomNav 视为"被遮挡"并触发既有的出场动画。
 */
function useModalBlocked(): boolean {
  const [blocked, setBlocked] = useState(false)

  useEffect(() => {
    const shell = document.getElementById('scroll-container')?.parentElement
    if (!shell) {
      return
    }

    const evaluate = () => {
      // 匹配任何全屏遮罩/侧拉栏背板：项目内 z-index 从 z-40 到 z-[200] 不一，
      // 但都以 `fixed inset-0` 为统一签名（backdrop 或容器），据此判定"弹窗开启"。
      const hit = shell.querySelector<HTMLElement>('.fixed.inset-0')
      setBlocked(!!hit)
    }

    evaluate()
    const observer = new MutationObserver(evaluate)
    observer.observe(shell, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class'],
    })
    return () => observer.disconnect()
  }, [])

  return blocked
}

/** 顶层单例：内部判断当前路由是否展示，非展示页做进/出场动画 */
export default function BottomNav() {
  useVisualViewportBottomOverlap()
  const { pathname } = useLocation()
  const { t } = useI18n()
  const modalBlocked = useModalBlocked()

  const visible = PRIMARY_PATHS.includes(pathname) && !modalBlocked

  const navItems: NavItem[] = [
    { href: '/', icon: HomeTabIcon, label: String(t.nav.home) },
    { href: '/activity', icon: EventTabIcon, label: String(t.nav.activity) },
    { href: '/market', icon: MarketTabIcon, label: String(t.nav.market) },
    { href: '/team', icon: TeamTabIcon, label: String(t.nav.team) },
    { href: '/assets', icon: WalletTabIcon, label: String(t.nav.assets) },
  ]

  function isNavActive(href: string): boolean {
    if (href === '/') {
      return pathname === '/'
    }
    return pathname === href || pathname.startsWith(`${href}/`)
  }

  return createPortal(
    <>
      {/* 屏幕底部柔性青色氛围光 */}
      <div
        aria-hidden
        className={`pointer-events-none fixed inset-x-0 bottom-0 z-40 h-36 bg-gradient-to-t from-[#5eead4]/[0.07] via-transparent to-transparent transition-opacity duration-300 ease-out ${
          visible ? 'opacity-100' : 'opacity-0'
        }`}
      />

      {/* 与 MobileAppShell 同宽：居中 + max-w-[430px] + 左右 1.25rem，与原 inset-x-5 一致 */}
      <div
        className={`fixed inset-x-0 z-50 flex justify-center transition-[bottom] duration-[420ms] ease-out ${
          visible ? '' : 'pointer-events-none'
        }`}
        style={{ bottom: 'var(--bottom-nav-offset)' }}
      >
        <div className="w-full max-w-[430px] px-5">
          <nav
            aria-label="Primary"
            aria-hidden={!visible}
            className={`relative flex w-full items-center justify-between rounded-full border border-white/[0.08] bg-[#0c0c0e]/70 p-1.5 shadow-[0_24px_70px_-16px_rgba(94,234,212,0.42),inset_0_1px_0_rgba(255,255,255,0.07)] backdrop-blur-2xl transition-[transform,opacity] duration-[420ms] ease-out will-change-transform ${
              visible
                ? 'translate-y-0 opacity-100'
                : 'translate-y-[calc(100%+var(--bottom-nav-offset))] opacity-0'
            }`}
          >
            {/* 顶边极细高光线（与首页所有玻璃卡片呼应） */}
            <span
              aria-hidden
              className="pointer-events-none absolute inset-x-12 top-0 h-px bg-gradient-to-r from-transparent via-[#5eead4]/50 to-transparent"
            />
            {navItems.map((item) => {
              const isActive = isNavActive(item.href)
              const Icon = item.icon
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  aria-label={item.label}
                  aria-current={isActive ? 'page' : undefined}
                  tabIndex={visible ? 0 : -1}
                  className={`relative flex items-center gap-2 rounded-full transition-all duration-300 ${
                    isActive
                      ? 'grad-mint-lime px-4 py-3 text-black shadow-[0_0_28px_rgba(94,234,212,0.5),inset_0_1px_0_rgba(255,255,255,0.35)]'
                      : 'h-11 w-11 justify-center px-0 text-gray-400 hover:bg-white/[0.05] hover:text-white'
                  }`}
                >
                  <Icon color={isActive ? '#000000' : '#9CA3AF'} className="h-5 w-5 shrink-0" />
                  {isActive && (
                    <span className="text-xs font-bold tracking-wide">{item.label}</span>
                  )}
                </Link>
              )
            })}
          </nav>
        </div>
      </div>
    </>,
    document.body,
  )
}
